import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const beaulieuSurMerEn: ChauffeurPage = {
  slug: "beaulieu-sur-mer",
  title: "VTC & private chauffeur in Beaulieu-sur-Mer",
  subtitle:
    "Private chauffeur in Beaulieu-sur-Mer: Villa Kérylos, the Baie des Fourmis, the marina, La Réserve and a ~25 min transfer to Nice airport.",
  keywords: [
    "vtc beaulieu-sur-mer",
    "private chauffeur beaulieu-sur-mer",
    "villa kerylos chauffeur",
    "beaulieu nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Beaulieu-sur-Mer",
    subtitle:
      "The resort of quiet luxury, between Villa Kérylos and the Baie des Fourmis: a chauffeur for the seafront and the corniches.",
    keywords: [
      "VTC Beaulieu-sur-Mer",
      "Villa Kérylos chauffeur",
      "Nice airport transfer",
      "Cap-Ferrat chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Beaulieu-sur-Mer",
    badges: ["Villa Kérylos", "Marina", "~25 min from the airport"],
  },
  benefits: {
    heading: "Why a chauffeur in Beaulieu-sur-Mer",
    items: [
      {
        title: "Villa Kérylos & the seafront",
        description:
          "Drop-off at the foot of the Greek villa and the Baie des Fourmis, a seafront stroll at your own pace, pick-up with no parking to sort out along the shore.",
      },
      {
        title: "Quiet luxury & palaces",
        description:
          "La Réserve de Beaulieu and the Belle Époque villas: a hushed welcome and a chauffeur to match a discerning clientele.",
      },
      {
        title: "Marina & Cap-Ferrat",
        description:
          "From the marina to the Maurice Rouvier promenade over to Saint-Jean-Cap-Ferrat: short, comfortable links along the coast.",
      },
      {
        title: "~25 minutes from the airport",
        description:
          "The shortest of our towns’ runs to Nice airport, via the lower corniche or the A8, with flight tracking.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Beaulieu-sur-Mer",
    services: [
      {
        title: "Nice airport transfers",
        text: "Beaulieu-sur-Mer ⇄ Nice airport in about 25 minutes, with flight tracking and a personal welcome on arrival.",
      },
      {
        title: "Villa Kérylos & the Baie des Fourmis",
        text: "Drop-off in front of the Greek villa and along the bay, with waiting time for the visit and the walk.",
      },
      {
        title: "Palaces & dinners",
        text: "La Réserve de Beaulieu and the seafront tables: an evening there and back with no driving of your own.",
      },
      {
        title: "Marina & yachting",
        text: "Links from the marina for crews and guests, transfers to Monaco and the other ports along the coast.",
      },
      {
        title: "Hourly hire & corniches",
        text: "By the hour or by the day to link Cap-Ferrat, Villefranche, Èze and Monaco along the three corniches.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Villa Kérylos",
        description:
          "A reconstructed Greek villa on the edge of the Baie des Fourmis.",
      },
      {
        name: "Baie des Fourmis",
        description:
          "A sheltered cove and its promenade, the seaside heart of Beaulieu.",
      },
      {
        name: "The marina",
        description: "Beaulieu’s harbour, its quays and embarkations.",
      },
      {
        name: "La Réserve de Beaulieu",
        description: "A historic seafront palace hotel.",
      },
      {
        name: "Beaulieu-sur-Mer station",
        description: "A rail halt on the Nice ⇄ Menton and Monaco line.",
      },
      {
        name: "Maurice Rouvier promenade",
        description:
          "A shoreline path linking Beaulieu to Saint-Jean-Cap-Ferrat.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur for the seafront resort",
    subheading:
      "Beaulieu-sur-Mer keeps a hushed luxury, sheltered by the Baie des Fourmis and its microclimate.",
    paragraphs: [
      "Tucked between Villefranche and Cap-d’Ail, Beaulieu-sur-Mer lines up Belle Époque villas, a marina and palace hotels under a famously mild microclimate. Along the seafront and near Villa Kérylos, a chauffeur drops you as close as possible and stays reachable, which spares the hunt for a space in season.",
      "KS Rent Services applies fixed fares set in advance — including Beaulieu-sur-Mer ⇄ Nice airport — and hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, are rated 5.0/5 from 38 Google reviews.",
      "Beaulieu’s position, equidistant from Nice and Monaco, makes it an ideal base to range along the corniches: Cap-Ferrat next door, Èze above, Monaco a few minutes away. The chauffeur picks the route to suit the hour and the traffic.",
    ],
    bullets: [
      "Drop-off on the seafront, chauffeur reachable throughout",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Beaulieu-sur-Mer",
    steps: [
      "Share your programme: airport transfer, Villa Kérylos, marina or corniches.",
      "We confirm the vehicle and the fixed fare or the hourly hire.",
      "The chauffeur picks you up at your villa, hotel or the marina.",
      "Live adjustments to your timings and the traffic along the coast.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Beaulieu-sur-Mer"),
    {
      q: "How long does it take to reach Nice airport?",
      a: "About 25 minutes, the shortest of our runs, via the lower corniche or the A8. The chauffeur tracks your flight and sets the departure time accordingly.",
    },
    {
      q: "Can you take us to Villa Kérylos and wait?",
      a: "Yes. We drop you in front of the Greek villa, on the edge of the Baie des Fourmis, with waiting time included for the visit and the shoreline walk.",
    },
    {
      q: "Do you handle links with the marina and Cap-Ferrat?",
      a: "Yes, transfers from the marina for crews and guests, and short links to Saint-Jean-Cap-Ferrat, Villefranche and Monaco.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book early in high season and during yacht calls; last-minute requests are honoured subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the clientele of Beaulieu’s palaces and villas.",
    },
    {
      q: "Which vehicles do you offer for Beaulieu-sur-Mer?",
      a: "A Mercedes E- or S-Class sedan, a 7-seat van and an SUV, depending on the number of passengers and the level of comfort wanted.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfers from Nice Airport",
      href: "/en/chauffeur/airport",
    },
    { label: "Book a chauffeur in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Private chauffeur in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Beaulieu-sur-Mer",
    text: "Airport transfer, Villa Kérylos or the corniches: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Beaulieu-sur-Mer — car service 24/7",
    description:
      "Private car service in Beaulieu-sur-Mer: Villa Kérylos, Baie des Fourmis, marina and a ~25 min transfer to Nice airport. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/beaulieu-sur-mer",
  },
};

export default beaulieuSurMerEn;
